import { useCallback, useEffect, useRef } from "react";

const EASE = "cubic-bezier(0.32, 0.72, 0, 1)";
const reduced = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Bottom-sheet behaviour: drag down to dismiss (mobile) + animated close for every close path.
 * Attach `panelRef` to the sheet panel and `backdropRef` (optional) to the overlay.
 * Content inside an element with [data-sheet-scroll] only drags when scrolled to the top.
 */
export function useSheetDrag({ onClose, enabled = true }) {
  const panelRef = useRef(null);
  const backdropRef = useRef(null);
  const closing = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const requestClose = useCallback(() => {
    const el = panelRef.current;
    if (closing.current) return;
    closing.current = true;
    if (!el || reduced()) { onCloseRef.current?.(); return; }
    const isSheet = window.innerWidth < 640;
    el.style.animation = "none";
    el.style.transition = `transform 220ms ${EASE}, opacity 160ms ease-out`;
    el.style.transform = isSheet ? "translateY(100%)" : "translateY(6px) scale(0.98)";
    if (!isSheet) el.style.opacity = "0";
    if (backdropRef.current) {
      backdropRef.current.style.transition = "opacity 200ms ease-out";
      backdropRef.current.style.opacity = "0";
    }
    setTimeout(() => onCloseRef.current?.(), isSheet ? 210 : 150);
  }, []);

  useEffect(() => {
    const el = panelRef.current;
    if (!el || !enabled) return undefined;
    closing.current = false;
    let startY = 0, startT = 0, dy = 0, active = false, dragging = false;

    const onStart = (e) => {
      if (window.innerWidth >= 640 || e.touches.length !== 1) return;
      const scroller = e.target.closest?.("[data-sheet-scroll]");
      if (scroller && scroller.scrollTop > 0) return;
      if (e.target.closest?.("input, textarea, select, [data-no-drag]")) return;
      active = true; dragging = false; dy = 0;
      startY = e.touches[0].clientY; startT = Date.now();
    };
    const onMove = (e) => {
      if (!active) return;
      const d = e.touches[0].clientY - startY;
      if (!dragging) {
        if (d < -4) { active = false; return; }
        if (d < 6) return;
        dragging = true;
        el.style.animation = "none";
        el.style.transition = "none";
      }
      dy = Math.max(0, d - 6);
      e.preventDefault();
      el.style.transform = `translateY(${dy}px)`;
      if (backdropRef.current) backdropRef.current.style.opacity = String(Math.max(0.2, 1 - dy / 500));
    };
    const onEnd = () => {
      if (!active) return;
      active = false;
      if (!dragging) return;
      const v = dy / Math.max(1, Date.now() - startT);
      if (dy > Math.min(140, el.offsetHeight * 0.3) || v > 0.7) {
        requestClose();
      } else {
        el.style.transition = `transform 260ms ${EASE}`;
        el.style.transform = "translateY(0)";
        if (backdropRef.current) { backdropRef.current.style.transition = "opacity 200ms"; backdropRef.current.style.opacity = "1"; }
      }
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [enabled, requestClose]);

  return { panelRef, backdropRef, requestClose };
}
